// WhatsAppContentSelector.tsx - EXPANDED VERSION (5 Content Types with Redux)
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { shareContentLead } from '../../services/leadService';
import { downloadContentFile, contentBlobToFile, getAllContent, getContentByType } from '../../services/contentService';
import { getTemplates } from '../../services/templates';
import { getDigitalCard } from '../../services/digitalCardService';
import PopupComponent from '../../components/PopupComponent';
import logger from '../../utils/logger';
import toast from 'react-hot-toast';
import { WEB_APP_URL, BUCKET_URL } from '../../config/config';
// Redux imports
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { selectAllTemplates } from '../../store/slices/templateSlice';

interface Lead {
  _id: string;
  name?: string;
  phone?: string;
  [key: string]: any;
}

interface ContentItem {
  _id: string;
  name?: string;
  type: 'file' | 'page' | 'message' | 'template' | 'digitalCard';  // 🔥 Added digitalCard support
  details?: {
    title?: string;
    description?: string;
    message?: string;  // 🔥 ADD: message field for content details
    category?: string;  // For templates
    status?: string;    // For templates
    language?: string;  // For templates
  };
  files?: Array<{
    filePath: string;
    fileName: string;
    fileSize: string;
    uploadedAt: string;
    uploadedBy: string;
  }>;
  // Message content specific fields
  content?: string;
  // Template specific fields
  templateData?: any;
  // Digital Card specific fields
  digitalCardData?: {
    name: string;
    email: string;
    phone: string;
    designation?: string;
    companyName?: string;
    cardUrl?: string;
    uniqueLinkId?: string;
  };
}

interface WhatsAppContentSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onContentSelect: (processedMessage: string, templateId?: string) => void;  // 🔥 Added templateId parameter
  currentLead: Lead;
  addFiles?: (files: File[]) => void;
}

const WhatsAppContentSelector: React.FC<WhatsAppContentSelectorProps> = ({
  isOpen,
  onClose,
  onContentSelect,
  currentLead,
  addFiles,
}) => {
  // 🔥 SIMPLE API STATE - No Redux!
  const [allContent, setAllContent] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Redux for templates
  const dispatch = useAppDispatch();
  const cachedTemplates = useAppSelector(selectAllTemplates);
  
  // 🔥 File operations state (only for files)
  const [sendMode, setSendMode] = useState<'link' | 'upload'>('link');
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
  const [downloadProgress, setDownloadProgress] = useState<{[key: string]: number}>({});

  // Original state for UI
  const [selectedContent, setSelectedContent] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'message' | 'file' | 'page' | 'template' | 'digitalCard'>('message');  // 🔥 Added digitalCard tab
  const [searchInput, setSearchInput] = useState<string>('');
  const [cardShareMode, setCardShareMode] = useState<'link' | 'file'>('link');  // For digital card sharing

  // 🔥 DIRECT API FILTERING - No Redux selector
  const filteredContent = useMemo(() => {
    let filtered = allContent.filter(item => item.type === activeTab);
    if (searchInput.trim()) {
      const searchTerm = searchInput.toLowerCase();
      filtered = filtered.filter(item => {
        const name = item.name || item.details?.title || '';
        const description = item.details?.description || '';
        return name.toLowerCase().includes(searchTerm) || description.toLowerCase().includes(searchTerm);
      });
    }
    return filtered;
  }, [allContent, activeTab, searchInput]);

  // 🔥 SIMPLE API CALLS - Just like BulkShareModal
  useEffect(() => {
    if (isOpen) {
      const fetchAllContentTypes = async () => {
        setLoading(true);
        setError(null);
        try {
          logger.log('📥 Content Selector opened, fetching content...');

          // Get regular content (message, file, page)
          const contentResponse = await getAllContent({ page: 1, perPage: 500 });
          const contentItems = contentResponse.data.data || [];

          // Get WhatsApp templates
          const templatesResponse = await getTemplates({ page: 1, perPage: 500 });
          const templateItems = templatesResponse.data.data || [];

          // Transform templates to match ContentItem structure
          const transformedTemplates = templateItems.map((template: any) => ({
            _id: template._id,
            name: template.name,
            type: 'template' as const,
            details: {
              title: template.name,
              description: template.components?.find((c: any) => c.type === 'BODY')?.text || '',
              category: template.category,
              status: template.status,
              language: template.language,
            },
            // Store full template data for later use
            templateData: template,
          }));

          // Get Digital Card
          let digitalCardItem: ContentItem[] = [];
          try {
            const digitalCardResponse = await getDigitalCard();
            const digitalCard = digitalCardResponse.data.data;

            if (digitalCard && !digitalCard.isDeleted) {
              digitalCardItem = [{
                _id: digitalCard._id,
                name: digitalCard.name,
                type: 'digitalCard' as const,
                details: {
                  title: digitalCard.name,
                  description: `${digitalCard.designation || ''} ${digitalCard.companyName ? `at ${digitalCard.companyName}` : ''}`.trim() || 'Digital Business Card',
                },
                digitalCardData: {
                  name: digitalCard.name,
                  email: digitalCard.email,
                  phone: digitalCard.phone,
                  designation: digitalCard.designation,
                  companyName: digitalCard.companyName,
                  cardUrl: digitalCard.cardUrl,
                  uniqueLinkId: digitalCard.uniqueLinkId,
                }
              }];
              logger.log('✅ Digital card loaded:', digitalCardItem[0]);
            } else {
              logger.log('ℹ️ No digital card available');
            }
          } catch (error) {
            logger.log('⚠️ Error fetching digital card (might not exist yet):', error);
          }

          logger.log('📊 Raw API responses:', {
            contentItems: contentItems.length,
            templateItems: templateItems.length,
            digitalCardItems: digitalCardItem.length,
            sampleContent: contentItems[0],
            sampleTemplate: transformedTemplates[0],
            sampleDigitalCard: digitalCardItem[0],
          });

          // Combine all content
          setAllContent([...contentItems, ...transformedTemplates, ...digitalCardItem]);

          logger.log('✅ Final content loaded:', {
            total: contentItems.length + transformedTemplates.length + digitalCardItem.length,
            byType: {
              message: contentItems.filter((item: any) => item.type === 'message').length,
              file: contentItems.filter((item: any) => item.type === 'file').length,
              page: contentItems.filter((item: any) => item.type === 'page').length,
              template: transformedTemplates.length,
              digitalCard: digitalCardItem.length,
            }
          });

        } catch (err: any) {
          logger.log('❌ Error fetching content:', err);
          setError(err.message || 'Failed to fetch content');
        } finally {
          setLoading(false);
        }
      };
      fetchAllContentTypes();
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedContent(null);
      setSelectedFiles(new Set());
      setDownloadingFiles(new Set());
      setDownloadProgress({});
      setSendMode('link');
    }
  }, [isOpen]);

  const handleTabChange = (newTab: typeof activeTab) => {
    logger.log('🔄 Tab changed to:', newTab);
    setActiveTab(newTab);
    setSelectedContent(null);
    setSelectedFiles(new Set());
  };

  // 🔥 File selection handlers
  const handleFileToggle = useCallback((fileKey: string) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileKey)) {
        newSet.delete(fileKey);
      } else {
        newSet.add(fileKey);
      }
      logger.log('📋 File selection updated:', newSet.size, 'files selected');
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const allFileKeys = new Set<string>();
    filteredContent.forEach(content => {
      if (content.files) {
        content.files.forEach(file => {
          allFileKeys.add(`${content._id}:${file.fileName}`);
        });
      }
    });
    setSelectedFiles(allFileKeys);
    logger.log('📋 Selected all files:', allFileKeys.size);
  }, [filteredContent]);

  const handleClearSelection = useCallback(() => {
    setSelectedFiles(new Set());
    logger.log('📋 Cleared file selection');
  }, []);

  // 🔥 CRITICAL FIX: Background download and upload with proper filePath
  const handleBulkFileUpload = useCallback(async () => {
    if (!addFiles || selectedFiles.size === 0) {
      logger.log('⚠️ Cannot upload: no addFiles function or no files selected');
      return;
    }

    const selectedFileArray = Array.from(selectedFiles).map(fileKey => {
      const [contentId, fileName] = fileKey.split(':');
      
      // Find file info from content data
      const contentItem = filteredContent.find(c => c._id === contentId);
      const fileInfo = contentItem?.files?.find(f => f.fileName === fileName);
      
      return { 
        contentId, 
        fileName, 
        fileKey,
        filePath: fileInfo?.filePath,
        fileInfo
      };
    });

    const downloadedFiles: File[] = [];
    let successCount = 0;

    logger.log(`🚀 Starting bulk download: ${selectedFileArray.length} files`);
    toast.loading(`Downloading ${selectedFileArray.length} files...`, { id: 'bulk-download' });

    for (const { contentId, fileName, fileKey, filePath } of selectedFileArray) {
      try {
        setDownloadingFiles(prev => new Set([...prev, fileKey]));
        setDownloadProgress(prev => ({ ...prev, [fileKey]: 10 }));

        if (!filePath) {
          throw new Error(`No file path found for ${fileName}`);
        }

        logger.log(`📥 Downloading: ${fileName}`);
        const blob = await downloadContentFile(filePath, fileName);
        setDownloadProgress(prev => ({ ...prev, [fileKey]: 70 }));

        if (!blob || blob.size === 0) {
          throw new Error(`Downloaded file is empty: ${fileName}`);
        }

        const file = contentBlobToFile(blob, fileName);
        downloadedFiles.push(file);
        
        setDownloadProgress(prev => ({ ...prev, [fileKey]: 100 }));
        successCount++;

        logger.log(`✅ Downloaded: ${fileName} (${file.size} bytes, ${file.type})`);

      } catch (error: any) {
        logger.log(`❌ Failed to download: ${fileName}`, error);
        toast.error(`Failed to download: ${fileName}`);
        setDownloadProgress(prev => ({ ...prev, [fileKey]: 0 }));
      } finally {
        setDownloadingFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(fileKey);
          return newSet;
        });
      }
    }

    toast.dismiss('bulk-download');

    if (downloadedFiles.length > 0) {
      logger.log(`📤 Adding ${downloadedFiles.length} files to upload queue`);
      addFiles(downloadedFiles);
      
      const successMessage = successCount === selectedFiles.size 
        ? `✅ All ${successCount} files added to upload queue`
        : `⚠️ ${successCount} of ${selectedFiles.size} files added to upload queue`;
      
      toast.success(successMessage);
      
      setSelectedFiles(new Set());
      setDownloadProgress({});
      onClose();
    } else {
      toast.error('❌ No files were downloaded successfully');
    }
  }, [selectedFiles, addFiles, onClose, filteredContent]);

  // 🔥 EXPANDED: Process all content types
  const processSelectedContent = async (content: ContentItem): Promise<{ message: string; templateId?: string }> => {
    logger.log('⚙️ Processing content type:', content.type, content);

    try {
      const leadName = currentLead.name || 'Customer';

      // Handle different content types
      switch (content.type) {
        case 'message': {
          // For message content, try multiple fields to find the actual content
          const messageContent = content.content ||
                                content.details?.message ||
                                content.details?.description ||
                                content.details?.title ||
                                '';

          logger.log('💬 Processing message content:', {
            messageContent,
            contentFields: {
              content: content.content,
              'details.message': content.details?.message,
              'details.description': content.details?.description,
              'details.title': content.details?.title
            },
            fullContent: content
          });

          if (!messageContent) {
            logger.log('⚠️ No message content found, using fallback');
            return { message: 'Message content not available' };
          }

          // Replace placeholders with lead info
          const personalizedMessage = messageContent
            .replace(/\{\{name\}\}/gi, leadName)
            .replace(/\{\{phone\}\}/gi, currentLead.phone || '');

          logger.log('✅ Personalized message:', personalizedMessage);
          return { message: personalizedMessage };
        }

        case 'template': {
          // For templates, return the body text for preview and the template ID for sending
          logger.log('📋 Processing template:', content.templateData);

          const bodyComponent = content.templateData?.components?.find((c: any) => c.type === 'BODY');
          const previewText = bodyComponent?.text || content.details?.description || 'Template selected';

          logger.log('✅ Template ready to send:', {
            templateId: content._id,
            templateName: content.name,
            previewText
          });

          return {
            message: `📋 Template: ${content.name}\n\n${previewText}`,
            templateId: content._id
          };
        }

        case 'digitalCard': {
          // For digital cards, share as link or file based on mode
          logger.log('🎴 Processing digital card:', content.digitalCardData, 'Mode:', cardShareMode);

          const cardData = content.digitalCardData;
          if (!cardData) {
            throw new Error('Digital card data not available');
          }

          if (cardShareMode === 'link') {
            // Share as link
            if (!cardData.uniqueLinkId) {
              throw new Error('Digital card link not available. Please save your card first.');
            }

            const shareLink = `${WEB_APP_URL}/share/Card/${cardData.uniqueLinkId}`;
            const message = `Hi ${leadName},\n\nHere's my digital business card:\n${shareLink}`;

            logger.log('✅ Digital card link generated:', message);
            return { message };

          } else {
            // Share as file (PDF)
            if (!cardData.cardUrl) {
              throw new Error('Digital card PDF not available. Please save your card first.');
            }

            // Download and queue the PDF file
            try {
              const pdfUrl = `${BUCKET_URL}${cardData.cardUrl}`;
              const response = await fetch(pdfUrl);
              const blob = await response.blob();
              const file = contentBlobToFile(blob, `${cardData.name}-BusinessCard.pdf`);

              // Use addFiles to queue the PDF
              if (addFiles) {
                addFiles([file]);
                logger.log('✅ Digital card PDF queued for upload');
                return { message: '' };  // Empty message since we're uploading file
              } else {
                throw new Error('File upload not available');
              }
            } catch (error) {
              logger.log('❌ Failed to download digital card PDF:', error);
              throw new Error('Failed to download digital card PDF');
            }
          }
        }

        case 'file':
        case 'page':
        default: {
          // For files and pages, generate sharing link
          logger.log('🔗 Generating unique link for:', content.type);

          const body = {
            contentId: content._id,
            leadIds: [currentLead._id],
            performedAt: new Date(),
            contentType: content.type,
          };

          const response = await shareContentLead(body);

          if (!response?.data?.data?.uniqueLink) {
            throw new Error('No unique link generated');
          }

          const processedMessage = `Hi ${leadName},\n\nHere's the link to view:\n${response.data.data.uniqueLink}`;

          logger.log('✅ Generated link message:', processedMessage);
          return { message: processedMessage };
        }
      }

    } catch (error) {
      logger.log('❌ Error processing content:', error);
      throw error;
    }
  };

  const handleContentSelect = async () => {
    if (!selectedContent) {
      logger.log('⚠️ No content selected');
      toast.error('Please select content first');
      return;
    }

    try {
      logger.log('🎯 Handling content selection:', selectedContent);

      const content = filteredContent.find(item => item._id === selectedContent);
      if (!content) {
        logger.log('❌ Selected content not found:', selectedContent);
        logger.log('❌ Available content:', filteredContent.map(c => c._id));
        toast.error('Selected content not found');
        return;
      }

      logger.log('✅ Found selected content:', content);

      const result = await processSelectedContent(content);
      logger.log('📤 Processed result ready:', result);

      // 🔥 CRITICAL: Call the parent function to populate typing area
      // For templates, pass both message and templateId
      logger.log('🚀 CALLING onContentSelect with:', result);
      onContentSelect(result.message, result.templateId);

      logger.log('✅ Content selection completed, closing modal');
      onClose();

    } catch (error) {
      logger.log('❌ Error selecting content:', error);
      toast.error('Failed to process selected content: ' + (error as Error).message);
    }
  };

  const handleContentClick = (contentId: string) => {
    // All content types are clickable for selection
    logger.log('👆 Content clicked:', {
      activeTab,
      contentId,
      sendMode: activeTab === 'file' ? sendMode : 'N/A'
    });
    setSelectedContent(contentId);
    logger.log('✅ Selected content set to:', contentId);
  };

  const renderContentPreview = (content: ContentItem) => {
    switch (content.type) {
      case 'file':
        if (content.files && content.files.length > 0) {
          return `File: ${content.files[0].fileName}`;
        }
        return 'File content';

      case 'message':
        const msgContent = content.content ||
                          content.details?.message ||
                          content.details?.description ||
                          content.details?.title ||
                          'Text message';
        logger.log('📝 Message preview for', content._id, ':', msgContent);
        return msgContent;

      case 'page':
        return content.details?.description || 'Web page content';

      case 'template':
        return content.details?.description || 'WhatsApp template';

      case 'digitalCard':
        return content.details?.description || 'Digital Business Card';

      default:
        return content.details?.description || 'No preview available';
    }
  };

  // Enhanced content card with file upload support
  const renderContentCard = (content: ContentItem) => {
    const isSelected = content._id === selectedContent;
    const title = content.name || content.details?.title || 'Untitled';
    const preview = renderContentPreview(content);

    // Handle template content with special styling
    if (content.type === 'template' && content.templateData) {
      const template = content.templateData;
      const bodyComponent = template.components?.find((c: any) => c.type === 'BODY');
      const headerComponent = template.components?.find((c: any) => c.type === 'HEADER');
      const footerComponent = template.components?.find((c: any) => c.type === 'FOOTER');
      const buttons = template.components?.find((c: any) => c.type === 'BUTTONS')?.buttons || [];

      return (
        <div
          key={content._id}
          onClick={() => handleContentClick(content._id)}
          style={{
            padding: '16px',
            border: `2px solid ${isSelected ? '#3FAEFD' : '#E5E7EB'}`,
            borderRadius: '12px',
            backgroundColor: isSelected ? '#F0F9FF' : 'white',
            cursor: 'pointer',
            marginBottom: '12px',
            boxShadow: isSelected ? '0 4px 12px rgba(63, 174, 253, 0.2)' : '0 2px 4px rgba(0,0,0,0.05)',
            transition: 'all 0.2s ease',
          }}
        >
          {/* Template Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#1F2937', marginBottom: '4px' }}>
                📋 {title.split('_').join(' ')}
              </div>
              <div style={{ fontSize: '12px', color: '#6B7280' }}>
                {content.details?.category} • {content.details?.language?.split('_').join(' ')}
              </div>
            </div>
            <div style={{
              fontSize: '10px',
              color: content.details?.status === 'APPROVED' ? '#059669' : '#D97706',
              backgroundColor: content.details?.status === 'APPROVED' ? '#ECFDF5' : '#FEF3C7',
              padding: '4px 8px',
              borderRadius: '8px',
              textTransform: 'uppercase',
              fontWeight: 700,
            }}>
              {content.details?.status}
            </div>
          </div>

          {/* Template Body Preview */}
          <div style={{
            padding: '12px',
            backgroundColor: '#F9FAFB',
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            marginBottom: '8px'
          }}>
            {headerComponent && (
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                {headerComponent.text || headerComponent.format || 'Header'}
              </div>
            )}
            <div style={{ fontSize: '13px', color: '#4B5563', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
              {bodyComponent?.text || preview}
            </div>
            {footerComponent && (
              <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '8px', fontStyle: 'italic' }}>
                {footerComponent.text}
              </div>
            )}
          </div>

          {/* Template Buttons */}
          {buttons.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {buttons.map((button: any, idx: number) => (
                <div
                  key={idx}
                  style={{
                    fontSize: '12px',
                    color: '#3FAEFD',
                    backgroundColor: '#F0F9FF',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    textAlign: 'center',
                    fontWeight: 500,
                    border: '1px solid #BFDBFE'
                  }}
                >
                  {button.text}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Handle digital card content with special styling
    if (content.type === 'digitalCard' && content.digitalCardData) {
      const cardData = content.digitalCardData;

      return (
        <div
          key={content._id}
          onClick={() => handleContentClick(content._id)}
          style={{
            padding: '16px',
            border: `2px solid ${isSelected ? '#3FAEFD' : '#E5E7EB'}`,
            borderRadius: '12px',
            backgroundColor: isSelected ? '#F0F9FF' : 'white',
            cursor: 'pointer',
            marginBottom: '12px',
            boxShadow: isSelected ? '0 4px 12px rgba(63, 174, 253, 0.2)' : '0 2px 4px rgba(0,0,0,0.05)',
            transition: 'all 0.2s ease',
          }}
        >
          {/* Card Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#1F2937', marginBottom: '4px' }}>
                🎴 {cardData.name}
              </div>
              <div style={{ fontSize: '12px', color: '#6B7280' }}>
                {cardData.designation} {cardData.companyName && `• ${cardData.companyName}`}
              </div>
            </div>
            <div style={{
              fontSize: '10px',
              color: '#059669',
              backgroundColor: '#ECFDF5',
              padding: '4px 8px',
              borderRadius: '8px',
              textTransform: 'uppercase',
              fontWeight: 700,
            }}>
              CARD
            </div>
          </div>

          {/* Card Body Preview */}
          <div style={{
            padding: '12px',
            backgroundColor: '#F9FAFB',
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            marginBottom: '8px'
          }}>
            <div style={{ fontSize: '13px', color: '#4B5563', lineHeight: '1.5' }}>
              📧 {cardData.email}<br />
              📞 {cardData.phone}
            </div>
          </div>

          {/* Share Mode Indicator */}
          <div style={{
            fontSize: '12px',
            color: '#3FAEFD',
            backgroundColor: '#F0F9FF',
            padding: '6px 12px',
            borderRadius: '6px',
            textAlign: 'center',
            fontWeight: 500,
            border: '1px solid #BFDBFE'
          }}>
            {cardShareMode === 'link' ? '🔗 Share as Link' : '📄 Share as PDF'}
          </div>
        </div>
      );
    }

    // Handle file content differently in upload mode
    if (activeTab === 'file' && sendMode === 'upload' && content.files) {
      return (
        <div key={content._id} style={{
          padding: '16px',
          border: '2px solid #E5E7EB',
          borderRadius: '12px',
          backgroundColor: 'white',
          marginBottom: '12px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <div style={{ 
            fontSize: '15px', 
            fontWeight: 600, 
            marginBottom: '12px',
            color: '#1F2937',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            📁 {title}
          </div>
          
          {/* File list with checkboxes */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {content.files?.map((fileInfo, index) => {
              const fileKey = `${content._id}:${fileInfo.fileName}`;
              const isDownloading = downloadingFiles.has(fileKey);
              const progress = downloadProgress[fileKey] || 0;
              const isFileSelected = selectedFiles.has(fileKey);
              
              return (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  backgroundColor: isFileSelected ? '#F0F9FF' : '#F9FAFB',
                  borderRadius: '8px',
                  border: isFileSelected ? '2px solid #3FAEFD' : '1px solid #E5E7EB',
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="checkbox"
                    checked={isFileSelected}
                    onChange={() => handleFileToggle(fileKey)}
                    disabled={isDownloading}
                    style={{ 
                      cursor: isDownloading ? 'not-allowed' : 'pointer',
                      transform: 'scale(1.2)'
                    }}
                  />
                  
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                      📄 {fileInfo.fileName}
                    </div>
                    
                    {isDownloading && (
                      <div style={{ marginTop: '6px' }}>
                        <div style={{
                          width: '100%',
                          height: '4px',
                          backgroundColor: '#E5E7EB',
                          borderRadius: '2px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${progress}%`,
                            height: '100%',
                            backgroundColor: '#3FAEFD',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                        <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>
                          Downloading... {progress}%
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    
    // Regular card for all other modes
    return (
      <div
        key={content._id}
        onClick={() => handleContentClick(content._id)}
        style={{
          padding: '12px',
          border: `1px solid ${isSelected ? '#3FAEFD' : '#E5E7EB'}`,
          borderRadius: '8px',
          backgroundColor: isSelected ? '#F0F9FF' : 'white',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.2s ease',
          boxShadow: isSelected ? '0 2px 8px rgba(63, 174, 253, 0.15)' : '0 1px 2px rgba(0,0,0,0.05)',
          marginBottom: '8px'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '8px'
        }}>
          <div style={{
            fontSize: '15px',
            fontWeight: 600,
            color: '#1F2937',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1
          }}>
            {activeTab === 'message' && '💬'}
            {activeTab === 'file' && '📁'}
            {activeTab === 'page' && '📄'}
            {activeTab === 'template' && '📋'}
            {activeTab === 'digitalCard' && '🎴'}
            {' ' + title}
          </div>
          
          <div style={{
            fontSize: '10px',
            color: '#059669',
            backgroundColor: '#ECFDF5',
            padding: '3px 6px',
            borderRadius: '8px',
            textTransform: 'uppercase',
            fontWeight: 600,
            whiteSpace: 'nowrap'
          }}>
            {content.type}
          </div>
        </div>
        
        {/* Content Preview */}
        <div style={{
          fontSize: '13px',
          color: '#4B5563',
          lineHeight: '1.4',
          padding: '10px',
          backgroundColor: '#F9FAFB',
          borderRadius: '6px',
          border: '1px solid #E5E7EB',
          maxHeight: '100px',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {preview.length > 150 ? preview.substring(0, 150) + '...' : preview}
        </div>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div style={{ 
      textAlign: 'center', 
      padding: '40px', 
      color: '#6B7280',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1
    }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>📂</div>
      <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No content found</h3>
      <p style={{ margin: 0 }}>Try adjusting your search or switching tabs</p>
    </div>
  );

  // Calculate counts for 5 content types
  const counts = useMemo(() => ({
    message: allContent.filter(item => item.type === 'message').length,
    file: allContent.filter(item => item.type === 'file').length,
    page: allContent.filter(item => item.type === 'page').length,
    template: allContent.filter(item => item.type === 'template').length,
    digitalCard: allContent.filter(item => item.type === 'digitalCard').length,
  }), [allContent]);

  logger.log('🎯 WhatsAppContentSelector render state:', {
    isOpen,
    allContentCount: allContent.length,
    loading,
    error,
    counts,
    activeTab,
    filteredCount: filteredContent.length,
    currentLead: currentLead._id,
    sendMode,
    selectedFilesCount: selectedFiles.size
  });

  return (
    <PopupComponent
      title="Select Content"
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '70vh', maxHeight: '600px' }}>
        
        {/* Content Type Info */}
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#F8FAFC',
          borderRadius: '8px',
          border: '1px solid #E2E8F0',
          fontSize: '13px',
          color: '#64748B'
        }}>
          {activeTab === 'message' && '💬 Send pre-written text messages directly'}
          {activeTab === 'file' && '📁 Share files as links or upload them directly'}
          {activeTab === 'page' && '📄 Share web page content as links'}
          {activeTab === 'template' && '📋 Send approved WhatsApp templates to customers'}
          {activeTab === 'digitalCard' && '🎴 Share your digital business card as a link or PDF'}
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #E5E7EB',
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '4px'
        }}>
          {(['message', 'file', 'page', 'template', 'digitalCard'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              style={{
                padding: '12px 16px',
                backgroundColor: activeTab === tab ? '#3FAEFD' : 'transparent',
                color: activeTab === tab ? 'white' : '#6B7280',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '6px 6px 0 0',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
            >
              {tab === 'digitalCard' ? 'Digital Card' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              <span style={{ marginLeft: '8px', fontSize: '12px', opacity: 0.8 }}>
                ({counts[tab]})
              </span>
            </button>
          ))}
        </div>

        {/* Digital Card Mode Selector - Only for Digital Card */}
        {activeTab === 'digitalCard' && (
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '16px',
            padding: '4px',
            backgroundColor: '#F3F4F6',
            borderRadius: '8px'
          }}>
            <button
              onClick={() => setCardShareMode('link')}
              style={{
                flex: 1,
                padding: '8px 16px',
                backgroundColor: cardShareMode === 'link' ? '#3FAEFD' : 'transparent',
                color: cardShareMode === 'link' ? 'white' : '#6B7280',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                transition: 'all 0.2s ease'
              }}
            >
              🔗 Share Link
            </button>

            <button
              onClick={() => setCardShareMode('file')}
              style={{
                flex: 1,
                padding: '8px 16px',
                backgroundColor: cardShareMode === 'file' ? '#3FAEFD' : 'transparent',
                color: cardShareMode === 'file' ? 'white' : '#6B7280',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                transition: 'all 0.2s ease'
              }}
            >
              📄 Share PDF
            </button>
          </div>
        )}

        {/* File Mode Selector - Only for Files */}
        {activeTab === 'file' && (
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '16px',
            padding: '4px',
            backgroundColor: '#F3F4F6',
            borderRadius: '8px'
          }}>
            <button
              onClick={() => setSendMode('link')}
              style={{
                flex: 1,
                padding: '8px 16px',
                backgroundColor: sendMode === 'link' ? '#3FAEFD' : 'transparent',
                color: sendMode === 'link' ? 'white' : '#6B7280',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                transition: 'all 0.2s ease'
              }}
            >
              📎 Send Links
            </button>
            
            <button
              onClick={() => setSendMode('upload')}
              disabled={!addFiles}
              style={{
                flex: 1,
                padding: '8px 16px',
                backgroundColor: sendMode === 'upload' ? '#3FAEFD' : 'transparent',
                color: sendMode === 'upload' ? 'white' : addFiles ? '#6B7280' : '#D1D5DB',
                border: 'none',
                borderRadius: '6px',
                cursor: addFiles ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: 500,
                transition: 'all 0.2s ease'
              }}
            >
              📤 Upload Files
            </button>
          </div>
        )}
        
        {/* Bulk Actions for File Upload Mode */}
        {activeTab === 'file' && sendMode === 'upload' && (
          <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            marginBottom: '16px',
            padding: '12px',
            backgroundColor: '#F8FAFC',
            borderRadius: '8px',
            border: '1px solid #E2E8F0'
          }}>
            <button
              onClick={handleSelectAll}
              disabled={downloadingFiles.size > 0}
              style={{
                padding: '6px 12px',
                backgroundColor: 'white',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                cursor: downloadingFiles.size > 0 ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                color: '#374151'
              }}
            >
              Select All
            </button>
            
            <button
              onClick={handleClearSelection}
              disabled={selectedFiles.size === 0 || downloadingFiles.size > 0}
              style={{
                padding: '6px 12px',
                backgroundColor: 'white',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                cursor: (selectedFiles.size === 0 || downloadingFiles.size > 0) ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                color: '#374151'
              }}
            >
              Clear Selection
            </button>
            
            <div style={{ flex: 1 }} />
            
            <div style={{ fontSize: '12px', color: '#6B7280' }}>
              {selectedFiles.size} files selected
            </div>
            
            <button
              onClick={handleBulkFileUpload}
              disabled={selectedFiles.size === 0 || downloadingFiles.size > 0}
              style={{
                padding: '8px 16px',
                backgroundColor: selectedFiles.size === 0 || downloadingFiles.size > 0 ? '#D1D5DB' : '#10B981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: (selectedFiles.size === 0 || downloadingFiles.size > 0) ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                fontWeight: 500
              }}
            >
              {downloadingFiles.size > 0 
                ? `Downloading... (${downloadingFiles.size})` 
                : `📤 Upload Selected (${selectedFiles.size})`
              }
            </button>
          </div>
        )}

        {/* Search Input */}
        <div style={{ marginBottom: '16px' }}>
          <input
            type="text"
            placeholder={`Search ${activeTab}s...`}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>

        {/* Error Display */}
        {error && (
          <div style={{
            color: '#EF4444',
            textAlign: 'center',
            margin: '16px 0',
            padding: '12px',
            backgroundColor: '#FEF2F2',
            borderRadius: '6px',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div style={{ 
            padding: '40px 0',
            textAlign: 'center',
            color: '#6B7280',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1
          }}>
            <div style={{ marginBottom: '16px' }}>
              <i className="ri-loader-4-line" style={{ 
                fontSize: '32px', 
                animation: 'spin 1s linear infinite'
              }}></i>
            </div>
            <div>Loading...</div>
          </div>
        ) : (
          /* Content List */
          <div style={{ 
            flex: 1,
            overflowY: 'auto',
            padding: '8px 4px'
          }}>
            {filteredContent.length > 0 ? (
              filteredContent.map(renderContentCard)
            ) : (
              renderEmptyState()
            )}
          </div>
        )}

        {/* Action Buttons - For link/message/template/digitalCard modes */}
        {(activeTab === 'message' || activeTab === 'page' || activeTab === 'template' || activeTab === 'digitalCard' || (activeTab === 'file' && sendMode === 'link')) && (
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            padding: '12px 0',
            borderTop: '1px solid #E5E7EB',
            backgroundColor: 'white',
            position: 'sticky',
            bottom: 0
          }}>
            <button
              onClick={onClose}
              style={{
                padding: '10px 20px',
                backgroundColor: '#F3F4F6',
                color: '#374151',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleContentSelect}
              disabled={!selectedContent}
              style={{
                padding: '10px 20px',
                backgroundColor: selectedContent ? '#3FAEFD' : '#D1D5DB',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: selectedContent ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              {activeTab === 'message' ? 'Send Message' :
               activeTab === 'page' ? 'Send Link' :
               activeTab === 'template' ? 'Send Template' :
               activeTab === 'digitalCard' ? (cardShareMode === 'link' ? 'Share Card Link' : 'Share Card PDF') :
               'Send Link'}
            </button>
          </div>
        )}
      </div>
    </PopupComponent>
  );
};

export default WhatsAppContentSelector;